
import { NoseurObject } from "@rivtn/noseur";
import { BaseService, SanitizedResponse, IWebsite, IWebsitePage, Status, IWebsiteEvent } from "../BaseService";

export enum TournamentStatus {

    ENDED = "ENDED",
    DRAFT = "DRAFT",
    ONGOING = "ONGOING",
    UPCOMING = "UPCOMING",

}

export enum ParticipantType {

    TEAM = "TEAM",
    MEMBER = "MEMBER",

}

export interface IGallery {
    caption: string;
    created_at: string;
    external_id: string;
    action_label: string;
    media_location: string;
    action_location: string;
    extra_data: {
        tags: string[];
    } & NoseurObject<any>;
}

export interface IMicroTournamentEdition {
    name: string;
    status: string;
    logo_url: string;
    created_at: string;
    description: string;
    external_id: string;
    tournament_id: string;
    meta: {
        host_id?: string;
        host_name?: string;
        winner_name?: string;
        program_name?: string;
        no_of_matches?: number;
        primary_score?: string;
        host_public_id?: string;
        winner_currency?: string;
        result_announced?: boolean;
        price_pool_physical?: boolean;
        price_pool_monetary?: boolean;
        prize_pool_distributed?: boolean;
        total_expected_participants: number;
        total_registered_participants: number;
        initial_score_for_tie_breaking?: string;
        price_pool_amounts?: NoseurObject<number>;
        price_pool_physical_prize_available_in_all_countries?: boolean;
    };
}

export interface ITournamentEditionMatch {
    status: string;
    location: string;
    group_id: number;
    match_id: number;
    end_date: string;
    start_date: string;
    external_id: string;
    tournament_id: string;
    cancel_or_forfeit_reason: string;
    extra_data: { [key: string]: any; };
    tournament_edition: IMicroTournamentEdition;
    tournament_edition_registered_participant_ids: string[];
    scores: Record<string, Record<string, Record<string, number>>>;
}

export interface ITournamentEditionFaq {
    answer: string;
    question: string;
    external_id: string;
}

export interface ITournamentEditionScoring {
    name: string;
    type: string;
    external_id: string;
    scoring: { [key: string]: any; };
    extra_data: { [key: string]: any; };
}

export interface ITournamentEditionRegisteredParticipant {
    type: string;
    resource: any;
    external_id: string;
    tournament_edition_registered: {
        status: string;
        own_matches_detail: {
            matches_won: number;
            matches_lost: number;
            matches_draw: number;
        };
    };
}

export interface ITournamentEditionScoringRecord {
    position: number;
    external_id: string;
    scores: { [key: string]: any; };
    extra_data: { [key: string]: any; };
    participant: ITournamentEditionRegisteredParticipant;
}

export interface ITournamentEditionPrizePool {
    winner: any;
    type: string;
    title: string;
    logo_url: string;
    position: number;
    prize_type: string;
    external_id: string;
    currency_prizes: NoseurObject<number>;
    physical_prize?: {
        name?: string;
        image_url: string;
        detail_link?: string;
        how_to_claim?: string;
        prize_claimed: boolean;
        available_in_all_countries: boolean;
    };
}

// PROGRAM

export interface ILeaderboard {
    points: number;
    position: number;
    external_id: string;
    participant_type: ParticipantType;
    participant: {
        name: string;
        image_url: string;
        public_id: string;
        country_code: string;
    };
    stats: {
        win_rate: number;
        matches_won_count: number;
        matches_lost_count: number;
        matches_draw_count: number;
        matches_total_count: number;
    };
}

export interface IProgram {
    name: string;
    public_id: string;
    approved: boolean;
    physical: boolean;
    copyright: string;
    full_name: string;
    short_name: string;
    features: string[];
    external_id: string;
    platforms: string[];
    logo_urls: string[];
    description: string;
    banner_urls: string[];
    extra_data: NoseurObject<any>;
    payouts: NoseurObject<number>;
    stats: {
        no_of_teams: number;
        no_of_members: number;
        no_of_matches: number;
        no_of_trophies: number;
        no_of_tournaments: number;
        no_of_physical_rewards: number;
    };
}

export interface ITournamentEdition {
    name: string;
    logo_url?: string;
    program: IProgram;
    location?: string;
    created_at: string;
    banner_url?: string;
    external_id: string;
    description?: string;
    country_code: string;
    tournament_id: string;
    status: TournamentStatus;
    participant_members_count: number;
    physical_address_location: string;
    dates: {
        tournament_end_date?: string;
        tournament_start_date?: string;
        registration_end_date?: string;
        registration_start_date?: string;
        result_announcement_date?: string;
        prize_pool_distribution_end_date?: string;
        prize_pool_distribution_start_date?: string;
    };
    meta: {
        public_id?: string;
        host_name?: string;
        winner_name?: string;
        program_name?: string;
        primary_score?: string;
        winner_currency?: string;
        price_pool_physical?: boolean;
        price_pool_monetary?: boolean;
        total_expected_participants: number;
        total_registered_participants: number;
        initial_score_for_tie_breaking?: string;
        price_pool_amounts?: NoseurObject<number>;
        price_pool_physical_prize_available_in_all_countries?: boolean;
    };
}

export interface ITournament {
    name: string;
    public_id: string;
    description: string;
    external_id: string;
    extra_data: { [key: string]: any; };
    tournament_edition?: ITournamentEdition;
}

export interface ITournamentEditionWebsite extends IWebsite {
    rules: string;
    details: string;
    website_design_settings: any;
    extra_data: NoseurObject<any>;
    tournament_edition: IMicroTournamentEdition;
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    website_pages: {
        HOME: IWebsitePage;
        ABOUT: IWebsitePage;
        MERCH: IWebsitePage;
        EVENTS: IWebsitePage;
        GALLERY: IWebsitePage;
        TROPHIES: IWebsitePage;
        SCHEDULE: IWebsitePage;
        LEADERBOARD: IWebsitePage;
        PARTICIPANTS: IWebsitePage;
    };
    home_settings: {
        show_sponsors: boolean;
        show_top_review: boolean;
        enable_rivtn_ad: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
        show_top_participants: boolean;
    };
    events_settings: {
        enable_rivtn_ad: boolean;
    };
    merchandise_settings: {
        storefront_id?: string;
        enable_rivtn_ad: boolean;
    };
    participants_settings: {
        enable_rivtn_ad: boolean;
        show_current_participants: boolean;
        show_previous_participants: boolean;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_matches: boolean;
        show_ongoing_upcoming_matches: boolean;
    };
    gallery_settings: {
        enable_rivtn_ad: boolean;
    };
    trophies_settings: {
        enable_rivtn_ad: boolean;
        enable_cross_filter: boolean;
        show_monetary_prize: boolean;
        show_physical_prize: boolean;
    };
    leaderboard_settings: {
        enable_rivtn_ad: boolean;
        show_participants_form: boolean;
        show_participants_wld_record: boolean;
        custom_score_settings: NoseurObject<any>;
        show_all_custom_score_leaderboard: boolean;
    };
    about_settings: {
        info?: string;
        show_faqs: boolean;
        show_rules: boolean;
        show_reviews: boolean;
        show_details: boolean;
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        enable_rivtn_ad_on_faqs: boolean;
    };
}

// MEMBER

export interface IMember {
    age: number;
    tagline: string;
    username: string;
    external_id: string;
    country_code: string;
    profile_banner_url: string;
    profile_picture_url: string;
    social_accounts: NoseurObject<string>;
}

export interface IProfileWebsite extends IWebsite {
    member: IMember;
    created_at: string;
    external_id: string;
    display_name: string;
    profile_detail: string;
    extra_data: NoseurObject<any>;
    media: {
        LOGO: string[];
        BANNER: string[];
        PASSPORT: string[];
        HALF_BODY: string[];
        SELF_ANIMATION: string[];
    };
    website_pages: {
        HOME: IWebsitePage;
        MERCH: IWebsitePage;
        GALLERY: IWebsitePage;
        SCHEDULE: IWebsitePage;
        ACHIEVEMENTS: IWebsitePage;
        TEAM_HISTORY: IWebsitePage;
    };
    merchandise_settings: {
        storefront_id: boolean;
        enable_rivtn_ad: boolean;
    };
    team_history_settings: {
        team_settings: boolean;
        enable_rivtn_ad: boolean;
        show_current_teams: boolean;
        show_previous_teams: boolean;
    };
    home_settings: {
        show_stats: boolean;
        show_sponsors: boolean;
        show_schedules: boolean;
        enable_rivtn_ad: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
    };
    achievement_settings: {
        enable_rivtn_ad: boolean;
        show_trophies_graphs: boolean;
        show_winnings_trails: boolean;
        show_winnings_graphs: boolean;
        show_trophies_trails: boolean;
        show_leaderboard_trails: boolean;
        show_leaderboard_graphs: boolean;
        show_team_trophies_graphs: boolean;
        show_team_trophies_trails: boolean;
        show_team_winnings_trails: boolean;
        show_team_winnings_graphs: boolean;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_team_matches: boolean;
        show_past_personal_matches: boolean;
        show_ongoing_upcoming_team_matches: boolean;
        show_ongoing_upcoming_personal_matches: boolean;
    };
};

export interface ITeamHistory {
    name: string;
    status: Status;
    logo_url: string;
    public_id: string;
    exit_date: string;
    join_date: string;
    external_id: string;
    country_code: string;
    extra_data: NoseurObject<any>;
    team_extra_data: NoseurObject<any>;
}

// TEAM

export interface ITeam {
    name: string;
    logo_url: string;
    public_id: string;
    description: string;
    external_id: string;
    country_code: string;
}

export interface ITeamMember {
    team_id: string;
    member_id: string;
    join_date: string;
    exit_date?: string;
    external_id: string;
    display_name: string;
    profile_details: string;
    passport_picture_url: string;
    half_body_picture_url: string;
    extra_data: Record<string, any>;
    status: "ACTIVE" | "PAST" | string;
    social_accounts: Record<string, string>;
    role: "MEMBER" | "MODERATOR" | "OWNER" | string;
    settings: {
        country: string;
        state_region: string;
        show_link_to_profile: boolean;
        show_recent_achievements: boolean;
    };
}

export interface ITeamNews {
    title: string;
    content: string;
    updated_at: string;
    external_id: string;
    extra_data: {
        category?: string;
        featured_image: string;
    };
}

export interface ITeamNotification {
    type: string;
    title: string;
    message: string;
    created_at: string;
    external_id: string;
    image_location: string;
}

export interface ITeamWebsite extends IWebsite {
    team: ITeam;
    owns_copyright: boolean;
    extra_data: NoseurObject<any>;
    website_pages: {
        HOME: IWebsitePage;
        NEWS: IWebsitePage;
        MERCH: IWebsitePage;
        GALLERY: IWebsitePage;
        MEMBERS: IWebsitePage;
        SCHEDULE: IWebsitePage;
        ACTIVITIES: IWebsitePage;
        ACHIEVEMENTS: IWebsitePage;
    };
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    home_settings: {
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_schedules: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
    };
    news_settings: {
        enable_rivtn_ad: boolean;
    };
    members_settings: {
        enable_rivtn_ad: boolean;
        show_previous_team_members: boolean;
    };
    merchandise_settings: {
        storefront_id: string;
        enable_rivtn_ad: boolean;
    };
    activities_settings: {
        show_trophies: boolean;
        enable_rivtn_ad: boolean;
        show_timeline_graph: boolean;
        show_members_trails: boolean;
        show_members_graphs: boolean;
        show_timeline_trails: boolean;
        show_activities_graph: boolean;
        show_tournament_trails: boolean;
        show_tournament_graphs: boolean;
        show_activities_trails: boolean;
        show_achievements_trails: boolean;
        show_achievements_graphs: boolean;
    };
    gallery_settings: {
        enable_rivtn_ad: boolean;
    };
    schedule_settings: {
        enable_rivtn_ad: boolean;
        show_past_matches: boolean;
        show_ongoing_upcoming_matches: boolean;
        show_past_team_members_matches: boolean;
        show_ongoing_upcoming_team_members_matches: boolean;
    };
}

// HOST

export interface Host {
    name: string;
    homepage: string;
    logo_url: string;
    public_id: string;
    created_at: string;
    short_info: string;
    external_id: string;
    country_code: string;
    meta: NoseurObject<any>;
}

export interface IHostWebsite extends IWebsite {
    host: Host;
    host_id: string;
    website_design_settings: any;
    extra_data: NoseurObject<any>;
    media: {
        LOGO: string[];
        BANNER: string[];
    };
    website_pages: {
        BLOG: IWebsitePage;
        HOME: IWebsitePage;
        ABOUT: IWebsitePage;
        MERCH: IWebsitePage;
        EVENTS: IWebsitePage;
        TOURNAMENTS: IWebsitePage;
    };
    emails_and_phones?: {
        email: string;
        phone: string;
    }[];
    physical_addresses?: {
        street: string;
        city: string;
        state: string;
        postal_code: string;
        country: string;
    }[];
    events_settings: {
        enable_rivtn_ad: boolean;
    };
    merchandise_settings: {
        storefront_id: string;
        enable_rivtn_ad: boolean;
    };
    hero_settings: {
        enable_rivtn_ad: boolean;
    };
    mobile_app_pages: Record<string, {
        label: string;
        enabled: boolean;
    }>;
    tournaments_page_settings: {
        enable_rivtn_ad: boolean;
        show_ended_tournament: boolean;
        show_closed_tournament: boolean;
        show_future_tournament: boolean;
        show_ongoing_tournament: boolean;
        show_cancelled_tournament: boolean;
    };
    about_settings: {
        info: string;
        show_people: boolean;
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_physical_address: boolean;
        show_contact_emails_and_phones: boolean;
    };
    home_settings: {
        show_sponsors: boolean;
        enable_rivtn_ad: boolean;
        show_schedules: boolean;
        show_top_gallery: boolean;
        show_twitter_feeds: boolean;
        show_top_activities: boolean;
        show_twitch_streams: boolean;
        show_instagram_feeds: boolean;
        show_top_tournaments: boolean;
        show_top_best_tournament_reviews: boolean;
    }; blog_settings: {
        enable_rivtn_ad_among_comments: boolean;
        enable_rivtn_ad_on_articles_page: boolean;
        enable_rivtn_ad_on_categories_page: boolean;
    };
    mobile_design_settings: {
        design_id: string;
        scheme_settings: Record<string, any> | null;
    };
}

export interface IHostManager {
    role: string;
    status: string;
    host_id: string;
    member_id: string;
    join_date: string;
    exit_date?: string;
    external_id: string;
    display_name: string;
    display_role: string;
    profile_details: string;
    passport_picture_url: string;
    half_body_picture_url: string;
    extra_data: Record<string, any>;
    social_accounts: Record<string, string>;
    settings: {
        country?: string;
        state_region?: string;
        show_link_to_profile: boolean;
        hide_from_managers_page?: boolean;
        show_recent_achievements: boolean;
    };
}

export interface IHostBlogArticle {
    title: string;
    content: string;
    host_id: string;
    updated_at: string;
    external_id: string;
    host_category_id: string;
    extra_data: Record<string, any>;
}

export interface IHostBlogCategory {
    name: string;
    external_id: string;
    description: string;
    icon_location: string;
}

export class OmneHostService extends BaseService {

    protected static instance: OmneHostService;

    private constructor() {
        super();
    }

    static getInstance() {
        if (!OmneHostService.instance) OmneHostService.instance = new OmneHostService();
        return OmneHostService.instance;
    }

    // GENERAL
    getMethod(path: string) {
        if (path === "ho" || path === "host") return this.getHostWebsite.bind(this);
        if (path === "te" || path === "team") return this.getTeamWebsite.bind(this);
        if (path === "pr" || path === "program") return this.getProgramDetails.bind(this);
        if (path === "to" || path === "tournament") return this.getTournamentEditionWebsite.bind(this);
        return this.getProfileWebsite.bind(this);
    }

    // PROGRAM

    getProgramDetails(programIdOrName: string, query?: any): Promise<SanitizedResponse<IProgram>> {
        return this.report(this.transport.get(`/v1/program/${programIdOrName}`, { query }));
    }

    queryProgramLeaderboard(programId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ILeaderboard>> {
        return this.mockResolve(100, {
            data: {
                succuss: true,
                message: "Success",
                data: {
                    size: 20,
                    total_elements: 5000,
                    content: [
                        {
                            position: 1,
                            points: 5000,
                            external_id: "1",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Lagos Lions",
                                country_code: "NGA",
                                image_url: "https://i.redd.it/zlg7bbp362sd1.jpeg",
                            },
                            stats: {
                                win_rate: 80,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 100,
                            }
                        },
                        {
                            position: 2,
                            points: 4500,
                            external_id: "2",
                            participant_type: ParticipantType.TEAM,
                            participant: {
                                public_id: "P1",
                                name: "Vampires",
                                country_code: "NGA",
                                image_url: "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg?semt=ais_rp_progressive&w=740&q=80",
                            },
                            stats: {
                                win_rate: 70,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 98,
                            }
                        },
                        {
                            position: 3,
                            points: 4000,
                            external_id: "3",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Fighterz",
                                country_code: "NGA",
                                image_url: "https://img.freepik.com/free-vector/user-blue-gradient_78370-4692.jpg?semt=ais_rp_50_assets&w=740&q=80",
                            },
                            stats: {
                                win_rate: 70,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 90,
                            }
                        },
                        {
                            position: 4,
                            points: 3000,
                            external_id: "4",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Crazy Pandaz",
                                country_code: "NGA",
                                image_url: "https://cdn.pixabay.com/photo/2025/08/28/11/47/user-9801869_1280.png",
                            },
                            stats: {
                                win_rate: 60,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 80,
                            }
                        },
                        {
                            position: 5,
                            points: 4500,
                            external_id: "5",
                            participant_type: ParticipantType.TEAM,
                            participant: {
                                public_id: "P1",
                                name: "Vampires",
                                country_code: "NGA",
                                image_url: "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg?semt=ais_rp_progressive&w=740&q=80",
                            },
                            stats: {
                                win_rate: 70,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 98,
                            }
                        },
                        {
                            position: 6,
                            points: 4000,
                            external_id: "6",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Fighterz",
                                country_code: "NGA",
                                image_url: "https://img.freepik.com/free-vector/user-blue-gradient_78370-4692.jpg?semt=ais_rp_50_assets&w=740&q=80",
                            },
                            stats: {
                                win_rate: 70,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 90,
                            }
                        },
                        {
                            position: 7,
                            points: 3000,
                            external_id: "7",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Crazy Pandaz",
                                country_code: "NGA",
                                image_url: "https://cdn.pixabay.com/photo/2025/08/28/11/47/user-9801869_1280.png",
                            },
                            stats: {
                                win_rate: 60,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 80,
                            }
                        },
                        {
                            position: 7,
                            points: 5000,
                            external_id: "7",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Lagos Lions",
                                country_code: "NGA",
                                image_url: "https://i.redd.it/zlg7bbp362sd1.jpeg",
                            },
                            stats: {
                                win_rate: 80,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 100,
                            }
                        },
                        {
                            position: 9,
                            points: 4000,
                            external_id: "9",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Fighterz",
                                country_code: "NGA",
                                image_url: "https://img.freepik.com/free-vector/user-blue-gradient_78370-4692.jpg?semt=ais_rp_50_assets&w=740&q=80",
                            },
                            stats: {
                                win_rate: 70,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 90,
                            }
                        },
                        {
                            position: 10,
                            points: 3000,
                            external_id: "10",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Crazy Pandaz",
                                country_code: "NGA",
                                image_url: "https://cdn.pixabay.com/photo/2025/08/28/11/47/user-9801869_1280.png",
                            },
                            stats: {
                                win_rate: 60,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 80,
                            }
                        },
                        {
                            position: 11,
                            points: 5000,
                            external_id: "11",
                            participant_type: ParticipantType.MEMBER,
                            participant: {
                                public_id: "P1",
                                name: "Lagos Lions",
                                country_code: "NGA",
                                image_url: "https://i.redd.it/zlg7bbp362sd1.jpeg",
                            },
                            stats: {
                                win_rate: 80,
                                matches_won_count: 50,
                                matches_lost_count: 40,
                                matches_draw_count: 10,
                                matches_total_count: 100,
                            }
                        },
                    ]
                }
            }
        });
        //return this.report(this.transport.get("/v1/member/profile/website"));

    }

    // MEMBER

    getMember(janusId: string): Promise<SanitizedResponse<IProfileWebsite>> {
        return this.report(this.transport.get(`/v1/member/website/profile/${janusId}`));
    }

    getProfileWebsite(publicId: string, query?: any): Promise<SanitizedResponse<IProfileWebsite>> {
        return this.report(this.transport.get(`/v1/member/website/${publicId}`, { query }));
    }

    queryMemberGallery(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/member/website/${publicId}/gallery`, { query: this.normalizeParams(params) }));
    }

    queryMemberSchedule(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.get(`/v1/member/website/${publicId}/schedule`, { query: this.normalizeParams(params) }));
    }

    queryMemberTeamHistory(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITeamHistory>> {
        return this.report(this.transport.get(`/v1/member/website/${publicId}/team/history`, { query: this.normalizeParams(params) }));
    }

    // TEAM

    getTeamWebsite(publicId: string, query?: any): Promise<SanitizedResponse<ITeamWebsite>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}`, { query }));
    }

    queryTeamGallery(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/gallery`, { query: this.normalizeParams(params) }));
    }

    queryTeamSchedule(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/schedule`, { query: this.normalizeParams(params) }));
    }

    queryTeamMembers(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITeamMember>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/member`, { query: this.normalizeParams(params) }));
    }

    queryTeamNews(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITeamNews>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/news`, { query: this.normalizeParams(params) }));
    }

    queryTeamNotifications(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITeamNotification>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/notification`, { query: this.normalizeParams(params) }));
    }

    queryTeamTournaments(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.get(`/v1/team/website/${publicId}/tournament`, { query: this.normalizeParams(params) }), (response: SanitizedResponse<{ tournament_edition: ITournamentEdition; }>) => {
            if (!response.apiResponse.data?.content) return;
            response.content = response.content.map((c) => c.tournament_edition) as any;
            response.apiResponse.data.content = response.content;
        });
    }

    // HOST

    getHostWebsite(publicId: string, query?: any): Promise<SanitizedResponse<IHostWebsite>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}`, { query }), ({ apiResponse }: SanitizedResponse<IHostWebsite>) => {
            if (!apiResponse.data) return;
            apiResponse.data.design_settings = apiResponse.data.website_design_settings;
        });
    }

    queryHostTournamentEditions(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }) {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/tournament/edition`, { query: this.normalizeParams(params) }));
    }

    queryHostManagers(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IHostManager>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/manager`, { query: this.normalizeParams(params) }));
    }

    queryHostEvents(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IWebsiteEvent>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/event`, { query: this.normalizeParams(params) }));
    }

    queryHostBlogArticles(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IHostBlogArticle>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/blog/article`, { query: this.normalizeParams(params) }));
    }

    queryHostBlogCategories(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IHostBlogCategory>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/blog/category`, { query: this.normalizeParams(params) }));
    }

    queryHostTournaments(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournament>> {
        return this.report(this.transport.get(`/v1/host/website/${publicId}/tournament`, { query: this.normalizeParams(params) }));
    }

    // TOURNAMENT

    queryTournamentEditions(params: NoseurObject<any> = { page: 1, size: 20 }) {
        return this.report(this.transport.get("/v1/tournament/edition", { query: this.normalizeParams(params) }));
    }

    getTournamentEdition(publicId: string): Promise<SanitizedResponse<ITournamentEdition>> {
        return this.report(this.transport.get(`/v1/tournament/edition/${publicId}`));
    }

    queryTournamentOwnEditions(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }) {
        return this.report(this.transport.get(`/v1/tournament/edition/e/${publicId}`, { query: this.normalizeParams(params) }));
    }

    getTournamentEditionWebsite(publicId: string, query?: any): Promise<SanitizedResponse<ITournamentEditionWebsite>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}`, { query }), ({ apiResponse }: SanitizedResponse<ITournamentEditionWebsite>) => {
            if (!apiResponse.data) return;
            apiResponse.data.design_settings = apiResponse.data.website_design_settings;
        });
    }

    queryTournamentEditionEvents(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IWebsiteEvent>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/event`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionGallery(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<IGallery>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/gallery`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionSchedule(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionMatch>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/schedule`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionFaqs(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionFaq>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/faq`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionScorings(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionScoring>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/scoring`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionPrizePools(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionPrizePool>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/prize/pool`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionParticipants(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionRegisteredParticipant>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/participant`, { query: this.normalizeParams(params) }));
    }

    queryTournamentEditionScoringRecords(publicId: string, params: NoseurObject<any> = { page: 1, size: 20 }): Promise<SanitizedResponse<ITournamentEditionScoringRecord>> {
        return this.report(this.transport.get(`/v1/tournament/edition/website/${publicId}/scoring/record`, { query: this.normalizeParams(params) }));
    }

}
